import type { ScholarshipModel, ScholarshipPlayer } from '../../../../types/scholarships';
import { formatPlayerValue } from './scholarshipPercent';
import { compactClassLabel } from './scholarshipClasses';
import { rowActionLabel } from './openableProspect';

interface Props {
  player: ScholarshipPlayer;
  depthIndex: number;
  appearance: 'primary' | 'secondary';
  cardLabel: 'RHP' | 'LHP';
  scholarshipModel: ScholarshipModel;
  onClick: () => void;
}

export const PitchingStaffRow = ({
  player,
  depthIndex,
  appearance,
  cardLabel,
  scholarshipModel,
  onClick,
}: Props) => {
  const percent = formatPlayerValue(player, scholarshipModel);
  const playerLabel = `${player.firstName} ${player.lastName}`;
  // Same as the position cards: an attached player's class lives on their player record, so
  // reading player.class alone left the staff card showing a dash for every one of them.
  const classLabel = compactClassLabel(player);
  const metaLine = `${classLabel ?? '—'} · ${player.bats ?? '—'}/${player.throws ?? '—'}`;
  const isSecondary = appearance === 'secondary';

  return (
    <li
      className={`depth-chart-staff-row${isSecondary ? ' depth-chart-staff-row--secondary' : ''}`}
    >
      <button
        type="button"
        className="depth-chart-staff-row__body"
        onClick={onClick}
        /* Announced instead of the row's inner text, so the class has to be said here too. */
        aria-label={
          isSecondary
            ? `${rowActionLabel(player, playerLabel)}, ${classLabel ? `class ${classLabel}, ` : ''}secondary in ${cardLabel}`
            : `${rowActionLabel(player, playerLabel)}, ${classLabel ? `class ${classLabel}, ` : ''}rank ${depthIndex + 1} in ${cardLabel}`
        }
      >
        <span className="depth-chart-staff-row__name-block">
          <span className="depth-chart-staff-row__name">
            <span
              className={`depth-chart-staff-row__marker depth-chart-staff-row__marker--${appearance}`}
              aria-hidden="true"
              title={isSecondary ? 'Secondary position' : 'Primary position'}
            >
              {isSecondary ? '○' : '★'}
            </span>
            {playerLabel}
            {player.isDsa && (
              <span
                className="depth-chart-staff-row__dsa-badge"
                title="Designated Student Athlete — excluded from the roster limit"
              >
                DSA
              </span>
            )}
          </span>
          <span className="depth-chart-staff-row__meta">{metaLine}</span>
        </span>
        {/* Same as the position cards: a secondary shows its real figure, dimmed, rather
            than a dash that reads as "no scholarship". Staff totals count primaries only. */}
        <span
          className={[
            'depth-chart-staff-row__percent',
            isSecondary ? 'depth-chart-staff-row__percent--secondary' : '',
            percent.isWalkOn ? 'depth-chart-staff-row__percent--walkon' : '',
          ]
            .filter(Boolean)
            .join(' ')}
          title={
            isSecondary
              ? "Scholarship counts on this player's primary position, not toward this one"
              : percent.isWalkOn
                ? 'Walk-on — no scholarship awarded'
                : undefined
          }
        >
          {percent.text}
        </span>
      </button>
    </li>
  );
};
